import { cloneDeep, merge, mergeWith } from 'lodash';
import { useEffect, useReducer } from 'react';
import type { ValueOf } from '@common/utils/utilityTypes';
import type { ISchemaContainer } from '@common/validation/schemaContainer';
import type { ValidationErrors } from '../validation';
import { scrollValidationErrorIntoView } from '..';
import { getValidationErrorTranslation } from '../getValidationErrorTranslation';
import { emptyValidationErrors, validate } from '../validation';

type ArrayKeysOf<T, K = keyof T> = K extends keyof T ? (T[K] extends Array<unknown> ? K : never) : never;

type ArrayKeyData<T, K = keyof T> = K extends keyof T ? (T[K] extends Array<infer U> ? Nullable<U> : never) : never;

interface GetUpdatedTouchedObjectForArrayItemOptions<T extends FormState> {
    arrayItemDestinationIndex: number;
    data: ArrayKeyData<T>;
    name: ArrayKeysOf<T>;
    prevTouchedState: TouchedObject<T>;
}

type Nullable<T> = Partial<{ [P in keyof T]: Nullable<T[P]> | null }>;

type OnChangeManualInput<T extends FormState> = {
    manualValidationFn?: (name: NestedKeyOf<T>, value: T) => ValidationErrors;
    name: NestedKeyOf<T>;
    skipValidation?: boolean;
    value: InputValue<T>;
};

type NestedKeyOf<T, K extends keyof T = keyof T> = K extends string | number
    ? T[K] extends Array<infer U>
        ? U extends Record<string, unknown>
            ? `${K}.${number}.${NestedKeyOf<U>}`
            : T[K] extends Array<infer V>
              ? `${K}.${number}.${NestedKeyOf<V>}`
              : never
        : T[K] extends Record<string, unknown>
          ? `${K}.${NestedKeyOf<T[K]>}`
          : `${K}`
    : never;

type FocusEvent = React.FocusEvent<Element | HTMLInputElement | HTMLTextAreaElement, Element>;

type OnChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

type OnSubmitEvent = React.FormEvent<HTMLFormElement>;

type OnSubmitEventOrSaveType = OnSubmitEvent | SaveType;

type Target = EventTarget & (HTMLInputElement | HTMLTextAreaElement);

type TouchedObject<T extends FormState> = Record<NestedKeyOf<T>, boolean>;

type SaveType = 'draft' | 'final';

export type InputValue<T extends FormState> = string | string[] | number | boolean | Date | null | ValueOf<T>;

export type OnChangeEventOrManualInput<T extends FormState> = OnChangeEvent | OnChangeManualInput<T>;

export type FormState = Record<string | number, unknown> | object;

export type FormStateErrorSetter<T extends FormState> = (key: NestedKeyOf<T>, errorMessage: string) => void;

export interface FormOptions<T extends FormState> {
    draftValidationSchema?: ISchemaContainer<T>;
    initialValues: Nullable<T>;
    onChange?: (value: { name: string; value: InputValue<T> }) => Promise<void> | void;

    onReset?: () => void | Promise<void>;
    onSubmit: (values: T, saveType?: SaveType) => void | Promise<void>;
    validationSchema: ISchemaContainer<T>;
}

export interface FormResult<T extends FormState> {
    addArrayItem: (name: ArrayKeysOf<T>, data: ArrayKeyData<T>, atIndex?: number) => void;
    errors: {
        getMessage: (key: NestedKeyOf<T>, arrayIndex?: number) => string | undefined;
        has: (key: NestedKeyOf<T>) => boolean;
        hasErrorsForKeyWithArray: (arrayKey: ArrayKeysOf<T>) => boolean;
        validationErrors: ValidationErrors;
    };
    handleBlur: (event: FocusEvent) => void;
    handleChange: (eventOrValue: OnChangeEventOrManualInput<T> | OnChangeManualInput<T>[]) => void;
    handleReset: () => void | Promise<void>;
    handleResetField: (name: NestedKeyOf<T>) => void;
    handleSubmit: (eventOrSaveType: OnSubmitEventOrSaveType | null, avoidFormReset?: boolean) => void | Promise<void>;
    initialValues: Nullable<T>;
    isSubmitting: boolean;
    removeArrayItem: (name: ArrayKeysOf<T>, atIndex: number) => void;
    touched: TouchedObject<T>;
    validateForm: () => boolean;
    values: T;
}

const hasArrayIndexRegExp = new RegExp(/^(?<key>[^.]+)\.(?<index>\d+)(\.(?<property>.+))?$/);

/**
 * useForm Hook
 *
 * This custom hook is used to create a form with validation. It manages form state, validation errors, touched state,
 * and submission state. It also provides handlers for form events.
 *
 * *IMPORTANT: Use the `src/frontend/components/contexts/form/FormProvider.tsx` over directly using this hook.
 * Because with the FormProvider it's possible to split a form up into smaller components who can access the form state and handlers, via the `useFormContext` hook.*
 *
 * Type parameter:
 * - T: A type that extends FormState. This should be an object type where each key represents a field in the form.
 *
 * Parameter:
 * - options: An object that includes the initial values for the form, a validation schema, and an onSubmit function.
 *
 * Returns:
 * - An object of type FormResult<T> that includes the form state, form handlers, and other form-related properties.
 *
 * Example usage:
 *
 * ```tsx
 * const form = useForm<MyFormState>({
 *     initialValues: { field1: '', field2: '' },
 *     validationSchema: myValidationSchema,
 *     onSubmit: (values) => console.log(values),
 * });
 * ```
 *
 * In this example, `MyFormState` would be an object type that represents the state of your form. The `form` variable
 * would then be an object that includes the form state, form handlers, and other form-related properties.
 */
export function useForm<T extends FormState>(options: FormOptions<T>): FormResult<T> {
    const [form, dispatch] = useReducer(formReducer<T>, {
        errors: emptyValidationErrors,
        initialValues: options.initialValues,
        isSubmitting: false,
        touched: convertToTouched(options.initialValues, false),
        values: options.initialValues as T,
    });

    const getErrorMessage = (key: NestedKeyOf<T>) =>
        form.touched[key] ? getValidationErrorTranslation(form.errors.get(key.toString())) : undefined;

    const validateField = (
        name: NestedKeyOf<T>,
        value: InputValue<T>,
        manualValidationFn?: OnChangeManualInput<T>['manualValidationFn']
    ) => {
        const updatedValues = getUpdatedFormValues(form.values, { name, value });

        if (manualValidationFn) {
            const manualValidationErrors = manualValidationFn(name, updatedValues);
            const manualValidationErrorMessage = manualValidationErrors.get(name);

            if (manualValidationErrorMessage) {
                dispatch({
                    payload: { errorMessage: manualValidationErrorMessage, fieldName: name },
                    type: 'ADD_VALIDATION_ERROR',
                });
                dispatch({ payload: { fieldName: name }, type: 'TOUCH_FIELD' });

                return;
            }
        }

        const { errors } = validate(options.validationSchema, updatedValues);
        const errorMessage = errors.get(name);

        if (errorMessage) {
            dispatch({ payload: { errorMessage, fieldName: name }, type: 'ADD_VALIDATION_ERROR' });
        } else {
            dispatch({ payload: { fieldName: name }, type: 'REMOVE_VALIDATION_ERROR' });
        }

        dispatch({ payload: { fieldName: name }, type: 'TOUCH_FIELD' });
    };

    const validateForm = (saveType?: SaveType) => {
        const schema =
            saveType === 'draft' && options.draftValidationSchema
                ? options.draftValidationSchema
                : options.validationSchema;

        const { errors: validationErrors, hasErrors } = validate<T>(schema, form.values);

        dispatch({ payload: { validationErrors }, type: 'SET_VALIDATION_ERRORS' });

        return hasErrors;
    };

    const updateFormState = ({
        name,
        value,
        skipValidation,
        manualValidationFn,
    }: {
        manualValidationFn?: OnChangeManualInput<T>['manualValidationFn'];
        name: NestedKeyOf<T>;
        skipValidation?: boolean;
        value: InputValue<T>;
    }) => {
        if (!skipValidation) {
            validateField(name, value, manualValidationFn);
        }

        options.onChange?.({ name, value });

        dispatch({ payload: { fieldName: name, value }, type: 'UPDATE_VALUES' });
    };

    const handleBlur = ({ target }: FocusEvent) => {
        const targetName = (target as HTMLInputElement).name;

        assertInputNameProperty(targetName, form.values);

        validateField(targetName as NestedKeyOf<T>, form.values[targetName as keyof T]);
    };

    const handleChange = (eventOrValue: OnChangeEventOrManualInput<T> | OnChangeManualInput<T>[]) => {
        if (Array.isArray(eventOrValue)) {
            eventOrValue.forEach(({ name, value, skipValidation }) => {
                updateFormState({
                    name,
                    skipValidation,
                    value,
                });
            });
        } else if (isOnChangeEvent(eventOrValue)) {
            const { target } = eventOrValue;

            assertInputNameProperty(target.name, form.values);

            const inputValue = getTargetInputValue(form.values, target);

            updateFormState({
                name: target.name as NestedKeyOf<T>,
                value: inputValue,
            });
        } else {
            updateFormState(eventOrValue);
        }
    };

    const handleAddArrayItem = (name: ArrayKeysOf<T>, data: ArrayKeyData<T>, atIndex?: number) => {
        let arrayItemDestinationIndex = 0;

        const prevArray = [...(form.values[name] as Array<ArrayKeyData<T>>)];

        arrayItemDestinationIndex = atIndex ?? prevArray.length;

        prevArray.splice(arrayItemDestinationIndex, 0, data);

        dispatch({
            payload: { fieldName: name as unknown as NestedKeyOf<T>, value: prevArray as InputValue<T> },
            type: 'UPDATE_VALUES',
        });

        dispatch({
            payload: {
                touched: getUpdatedTouchedObjectForArrayItem({
                    arrayItemDestinationIndex,
                    data,
                    name,
                    prevTouchedState: form.touched,
                }),
            },
            type: 'SET_TOUCHED_STATE',
        });
    };

    const handleRemoveArrayItem = (name: ArrayKeysOf<T>, atIndex: number) => {
        const filteredArray = (form.values[name] as unknown[]).filter((_, i) => i !== atIndex);

        dispatch({
            payload: { fieldName: name as unknown as NestedKeyOf<T>, value: filteredArray as InputValue<T> },
            type: 'UPDATE_VALUES',
        });

        // remove from touched state
        const clonedTouched = cloneDeep(form.touched);

        Object.keys(clonedTouched).forEach((key) => {
            if (key.startsWith(`${name.toString()}.${atIndex}`)) {
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete clonedTouched[key as keyof typeof clonedTouched];
            }
        });

        dispatch({ payload: { touched: clonedTouched }, type: 'SET_TOUCHED_STATE' });

        // remove from errors state
        dispatch({
            payload: {
                validationErrors: form.errors.filter(
                    (_value, key) => !key?.startsWith(`${name.toString()}`)
                ) as ValidationErrors,
            },
            type: 'SET_VALIDATION_ERRORS',
        });
    };

    const handleReset = async () => {
        dispatch({ type: 'RESET_FORM' });

        if (options.onReset) {
            await options.onReset();
        }
    };

    const handleResetField = (name: NestedKeyOf<T>) => {
        dispatch({ payload: { fieldName: name }, type: 'RESET_FIELD' });
    };

    const handleSubmit = async ({
        eventOrSaveType,
        latestValues,
        skipFormReset,
    }: {
        eventOrSaveType: OnSubmitEventOrSaveType | null;
        latestValues: T;
        skipFormReset?: boolean;
    }) => {
        const isEvent = eventOrSaveType ? isOnSubmitEvent(eventOrSaveType) : false;

        if (isEvent) {
            (eventOrSaveType as OnSubmitEvent).preventDefault();
        }

        dispatch({ payload: { isSubmitting: true }, type: 'SET_SUBMITTING_STATUS' });

        const hasErrors = validateForm(isEvent ? undefined : (eventOrSaveType! as SaveType));

        if (hasErrors) {
            dispatch({ payload: { touched: convertToTouched(latestValues, true) }, type: 'SET_TOUCHED_STATE' });

            delayedScrollToValidationError();
        } else {
            if (!isEvent) {
                await options.onSubmit(latestValues, eventOrSaveType! as SaveType);
            } else {
                await options.onSubmit(latestValues);
            }

            if (!skipFormReset) {
                dispatch({ type: 'RESET_FORM' });
            }
        }

        dispatch({ payload: { isSubmitting: false }, type: 'SET_SUBMITTING_STATUS' });
    };

    useEffect(() => {
        dispatch({ payload: { initialValues: options.initialValues as T }, type: 'SET_INITIAL_VALUES' });
        dispatch({ payload: { touched: convertToTouched(options.initialValues, false) }, type: 'SET_TOUCHED_STATE' });
    }, [options.initialValues]);

    return {
        addArrayItem: handleAddArrayItem,
        errors: {
            getMessage: getErrorMessage,
            has: (key) => form.touched[key] && form.errors.has(key as string),
            hasErrorsForKeyWithArray: (arrayKey: ArrayKeysOf<T>) =>
                Object.keys(form.errors.toObject()).some((key) => key.startsWith(arrayKey.toString())),
            validationErrors: form.errors,
        },
        handleBlur: (event) => handleBlur(event),
        handleChange: (eventOrValue) => handleChange(eventOrValue),
        handleReset: async () => await handleReset(),
        handleResetField: (name) => handleResetField(name),
        handleSubmit: async (eventOrSaveType: OnSubmitEventOrSaveType | null, skipFormReset?: boolean) =>
            await handleSubmit({
                eventOrSaveType,
                latestValues: form.values,
                skipFormReset,
            }),
        initialValues: options.initialValues,
        isSubmitting: form.isSubmitting,
        removeArrayItem: handleRemoveArrayItem,
        touched: form.touched,
        validateForm,
        values: form.values,
    };
}

function convertToTouched<T extends FormState>(
    formValues: T,
    isTouched: boolean,
    parentKey: string = ''
): TouchedObject<T> {
    return Object.keys(formValues).reduce((acc, key) => {
        const newKey = parentKey ? `${parentKey}.${key}` : key;
        const values = formValues[key as keyof T];

        if (typeof values === 'object' && values !== null && !Array.isArray(values)) {
            return { ...acc, ...convertToTouched(values, isTouched, newKey) };
        } else if (Array.isArray(values)) {
            return values.reduce(
                (result, item, index) => ({
                    ...result,
                    ...convertToTouched(item as FormState, isTouched, `${newKey}.${index}`),
                }),
                acc
            );
        } else {
            return { ...acc, [newKey]: isTouched };
        }
    }, {} as TouchedObject<T>);
}

function isKeyOfFormState<T extends FormState>(key: string | NestedKeyOf<T>, formState: T) {
    const nestedKeys = key.split('.');

    let currentObject = { ...formState };

    for (const nestedKey of nestedKeys) {
        if (nestedKey in currentObject) {
            currentObject = currentObject[nestedKey as keyof T] as T;
        } else {
            return false;
        }
    }

    return true;
}

function assertInputNameProperty<T extends FormState>(name: string, formState: T) {
    const isArrayField = hasArrayIndexRegExp.test(name);

    if (isArrayField) {
        return;
    }

    if (!name) {
        throw new Error('Input element must have a "name" property.');
    }

    const existsInFormState = isKeyOfFormState(name, formState);

    if (!existsInFormState) {
        throw new Error(`Property "${name}" does not exist in the formState.`);
    }
}

function getTargetInputValue<T extends FormState>(formState: T, target: Target): InputValue<T> {
    if (isHtmlInputElement(target)) {
        if (isCheckboxGroup(formState, target)) {
            return getCheckboxGroupValues(formState, target);
        } else if (target.type === 'checkbox') {
            return target.checked;
        }

        if (target.type === 'number') {
            return Number(target.value);
        }
    }

    return target.value;
}

function getCheckboxGroupValues<T extends FormState>(formState: T, target: HTMLInputElement) {
    const checkedBoxes = formState[target.name as keyof T] as string[];

    if (!target.checked) {
        return checkedBoxes.filter((v) => v !== target.value);
    }

    return checkedBoxes.includes(target.value)
        ? checkedBoxes
        : [...checkedBoxes, target.value].sort((a, b) => a.localeCompare(b));
}

function getUpdatedTouchedObjectForArrayItem<T extends FormState>({
    arrayItemDestinationIndex,
    data,
    name,
    prevTouchedState,
}: GetUpdatedTouchedObjectForArrayItemOptions<T>) {
    const key = `${name.toString()}.${arrayItemDestinationIndex}.`;

    const doesKeyExist = !!Object.keys(prevTouchedState).find((prop) => prop.startsWith(key));

    if (!doesKeyExist) {
        const newItems = Object.keys(data).reduce<TouchedObject<T>>(
            (result, currProperty) => ({ ...result, [`${key}${currProperty}`]: true }),
            prevTouchedState
        );

        return { ...prevTouchedState, ...newItems };
    } else {
        const existingKeyValueTuples = Object.entries(prevTouchedState)
            .filter(([k]) => {
                const index = Number(k.split('.')[1]);

                return k.startsWith(name.toString()) && index >= arrayItemDestinationIndex;
            })
            .sort(([a], [b]) => a.localeCompare(b))
            .reverse() as [string, boolean][];

        const updatedExistingObject = existingKeyValueTuples.reduce<TouchedObject<T>>((result, [k, v]) => {
            const [arrayKey, arrayIndex, property] = k.split('.');
            const newIndex = Number(arrayIndex) + 1;
            const newKey = `${arrayKey}.${newIndex}${property ? `.${property}` : ''}`;

            return { ...result, [newKey]: v };
        }, {} as TouchedObject<T>);

        const newItems = Object.keys(data).reduce<TouchedObject<T>>(
            (result, currProperty) => ({ ...result, [`${key}${currProperty}`]: true }),
            prevTouchedState
        );

        return { ...prevTouchedState, ...updatedExistingObject, ...newItems };
    }
}

function getUpdatedFormValues<T extends FormState>(
    oldFormState: T,
    updatedProperty: { name: NestedKeyOf<T>; value: InputValue<T> }
): T {
    const hasArray = hasArrayIndexRegExp.exec(updatedProperty.name);

    if (hasArray) {
        const { key, index, property } = hasArray.groups!;
        const arrayKey = key as keyof T;
        const arrayIndex = Number(index);
        const propertyName = property;

        const items = [...(oldFormState[arrayKey] as unknown[])];

        if (propertyName) {
            items[arrayIndex] = {
                ...(items[arrayIndex] as Record<string, unknown>),
                [propertyName]: updatedProperty.value,
            };
        } else {
            items[arrayIndex] = updatedProperty.value;
        }

        const updatedFormState = { ...oldFormState, [arrayKey]: items };

        return updatedFormState;
    } else {
        const updatedField = convertNestedKeyToObject(updatedProperty.name, updatedProperty.value);
        const updatedFormState = mergeAndOverrideArrayValues(oldFormState, updatedField);

        return updatedFormState;
    }
}

function convertNestedKeyToObject<T extends FormState>(
    nestedKey: NestedKeyOf<T>,
    value: InputValue<T>
): Record<keyof FormState, InputValue<T>> {
    return nestedKey
        .split('.')
        .reduceRight((acc, key, index, array) => (index === array.length - 1 ? { [key]: value } : { [key]: acc }), {});
}

function mergeAndOverrideArrayValues<T extends FormState>(destination: T, source: Partial<T>) {
    return mergeWith(cloneDeep(destination), source, (objValue, value) => {
        if (Array.isArray(objValue)) {
            return value;
        }
    });
}

function isOnChangeEvent<T extends FormState>(
    e: OnChangeEventOrManualInput<T>
): e is React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> {
    const event = e as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

    return !!event.target;
}

function isOnSubmitEvent(e: OnSubmitEventOrSaveType): e is React.FormEvent<HTMLFormElement> {
    const event = e as React.ChangeEvent<HTMLFormElement>;

    return !!event.target;
}

function isCheckboxGroup<T extends FormState>(formState: T, target: Target) {
    return target.type === 'checkbox' && Array.isArray(formState[target.name as keyof T]);
}

function isHtmlInputElement(target: Target): target is HTMLInputElement {
    return target instanceof HTMLInputElement;
}

/**
 * Delays the execution of `scrollValidationErrorIntoView`.
 * This function is necessary for ensuring that the validation error element
 * is available in the DOM so it can be scrolled into view.
 */
function delayedScrollToValidationError() {
    setTimeout(() => {
        scrollValidationErrorIntoView();
    }, 10);
}

type FormReducerState<T extends FormState> = {
    errors: ValidationErrors;
    initialValues: Nullable<T>;
    isSubmitting: boolean;
    touched: TouchedObject<T>;
    values: T;
};

type FormReducerAction<T extends FormState> =
    | { payload: { fieldName: NestedKeyOf<T>; value: InputValue<T> }; type: 'UPDATE_VALUES' }
    | { payload: { initialValues: Nullable<T> }; type: 'SET_INITIAL_VALUES' }
    | { type: 'RESET_FORM' }
    | { payload: { fieldName: NestedKeyOf<T> }; type: 'RESET_FIELD' }
    | { payload: { errorMessage: string; fieldName: NestedKeyOf<T> }; type: 'ADD_VALIDATION_ERROR' }
    | { payload: { fieldName: NestedKeyOf<T> }; type: 'REMOVE_VALIDATION_ERROR' }
    | { payload: { validationErrors: ValidationErrors }; type: 'SET_VALIDATION_ERRORS' }
    | { payload: { fieldName: NestedKeyOf<T> }; type: 'TOUCH_FIELD' }
    | { payload: { touched: TouchedObject<T> }; type: 'SET_TOUCHED_STATE' }
    | { payload: { isSubmitting: boolean }; type: 'SET_SUBMITTING_STATUS' };

function formReducer<T extends FormState>(
    form: FormReducerState<T>,
    action: FormReducerAction<T>
): FormReducerState<T> {
    switch (action.type) {
        case 'ADD_VALIDATION_ERROR':
            return { ...form, errors: form.errors.set(action.payload.fieldName, action.payload.errorMessage) };

        case 'SET_VALIDATION_ERRORS':
            return { ...form, errors: action.payload.validationErrors };

        case 'SET_INITIAL_VALUES':
            return {
                ...form,
                initialValues: action.payload.initialValues,
                values: action.payload.initialValues as T,
            };
        case 'UPDATE_VALUES':
            return {
                ...form,
                values: getUpdatedFormValues(form.values, {
                    name: action.payload.fieldName,
                    value: action.payload.value,
                }),
            };

        case 'REMOVE_VALIDATION_ERROR':
            return { ...form, errors: form.errors.remove(action.payload.fieldName) };
        case 'RESET_FORM':
            return {
                errors: emptyValidationErrors,
                initialValues: form.initialValues,
                isSubmitting: false,
                touched: convertToTouched(form.initialValues, false),
                values: form.initialValues as T,
            };
        case 'RESET_FIELD':
            return {
                ...form,
                touched: merge(form.touched, { [action.payload.fieldName]: false }),
                values: merge(form.values, {
                    [action.payload.fieldName]:
                        form.initialValues[action.payload.fieldName as keyof typeof form.initialValues],
                }),
            };

        case 'TOUCH_FIELD':
            return { ...form, touched: merge(form.touched, { [action.payload.fieldName]: true }) };

        case 'SET_TOUCHED_STATE':
            return { ...form, touched: action.payload.touched };

        case 'SET_SUBMITTING_STATUS':
            return { ...form, isSubmitting: action.payload.isSubmitting };

        default:
            throw Error('Unknown action type');
    }
}
